<template>
  <div>
    <base-card style="min-height: calc(100vh - 12rem)">
      <!-- Close button -->
      <div class="close-button flex justify-end">
        <base-icon
          icon="close"
          class="text-3xl cursor-pointer text-gray-500"
          :disabled="pendingRequest"
          @click="onCloseComponent"
        />
      </div>

      <!-- Basic info -->
      <div class="device-group-basics grid grid-cols-5 gap-x-5 mt-5">
        <!-- Group info -->
        <base-card class="device-group-info 2xl:col-span-2 col-span-3">
          <!-- Header -->
          <div class="device-group-info-header flex justify-between items-center mt-3 mb-4">
            <h2 class="font-semibold text-2xl">
              {{ $t(`mygwf.components.Card.device_groups_info`) }}
            </h2>

            <!-- Action buttons -->
            <div class="flex space-x-6">
              <base-button-icon
                v-if="!isGroupEditable"
                icon="edit"
                @click="isGroupEditable = true"
              />
              <template v-else>
                <div class="flex justify-end space-x-2">
                  <base-button-icon
                    v-if="!isCreationMode"
                    icon="close"
                    tooltip="cancel"
                    @click="isGroupEditable = false"
                  />
                  <base-button-icon
                    icon="checkmark"
                    class="bg-primary hover:bg-primary disabled:opacity-50 text-white"
                    :tooltip="isCreationMode ? 'create' : 'save'"
                    :disabled="$v.form.$invalid || pendingRequest || pendingDeviceRequest"
                    @click="onGroupSubmit"
                  />
                </div>
              </template>
              <base-button-icon
                icon="delete"
                v-if="!isCreationMode"
                action="delete_device_group"
                class="bg-red-600 text-white hover:text-red-600"
                :loading="pendingDelete"
                :disabled="pendingRequest"
                @click="onGroupDelete"
              />
            </div>
          </div>

          <!-- Form -->
          <div
            :class="
              isCreationMode
                ? 'device-group-info-form'
                : 'device-group-info-form grid grid-cols-3 gap2'
            "
          >
            <div class="col-span-2">
              <fieldset>
                <label class="mb-0.5 block text-secondary-dark text-sm">{{
                  $t(`mygwf.components.Label.name`)
                }}</label>
                <base-input v-if="isGroupEditable" v-model.trim="form.name" type="text" />
                <template v-else>
                  <span class="break-all">{{ form.name }}</span>
                  <hr class="my-4" />
                </template>
              </fieldset>
              <fieldset style="min-inline-size: unset">
                <label class="mb-0.5 block text-secondary-dark text-sm">{{
                  $t(`mygwf.components.Label.description`)
                }}</label>
                <base-input v-if="isGroupEditable" v-model.trim="form.description" type="text" />
                <template v-else>
                  <p class="truncate">
                    {{ form.description ? form.description : '-' }}
                  </p>
                  <hr class="my-4" />
                </template>
              </fieldset>
              <fieldset :style="isGroupEditable ? '' : 'margin-bottom: 0.7px;'">
                <label class="mb-0.5 block text-secondary-dark text-sm">{{
                  $t(`mygwf.components.Label.company_name`)
                }}</label>
                <base-select
                  v-if="isCreationMode"
                  v-model="form.company"
                  ref="companySelect"
                  :disabled="isCreationMode && preloadedDevices.length"
                  :options="companies"
                  :optionType="{id: 'id', label: 'name'}"
                  allowClear
                />
                <template v-else>
                  {{ $store.getters['companies/getById'](form.company).name }}
                </template>
              </fieldset>
            </div>
            <!-- Report Cards -->
            <template v-if="!isCreationMode">
              <base-loader v-if="pendingComponentInit" fetching />
              <div v-else class="flex flex-col space-y-3 p-2 self-center place-self-end ml-4">
                <div class="flex flex-col 2xl:flex-row space-y-1 2xl:items-center 2xl:space-x-4">
                  <base-icon icon="volume2" class="text-blue-light text-xl" />
                  <div class="">
                    <label class="mb-0.5 block text-secondary-dark text-sm whitespace-nowrap">{{
                      $t(`mygwf.components.Label.total_volume`)
                    }}</label>
                    {{ aggregatedData.total_volume }}
                  </div>
                </div>

                <div class="flex flex-col 2xl:flex-row space-y-1 2xl:items-center 2xl:space-x-4">
                  <base-icon icon="error" class="text-orange-light text-xl" />
                  <div class="">
                    <label class="mb-0.5 block text-secondary-dark text-sm">{{
                      $t(`mygwf.components.Label.total_alerts`)
                    }}</label>
                    {{ aggregatedData.total_alerts }}
                  </div>
                </div>
              </div>
            </template>

            <!-- Delete button -->
          </div>
        </base-card>

        <!-- Map -->
        <div class="device-group-map 2xl:col-span-3 col-span-2 shadow">
          <base-map
            ref="map"
            :groups="[{list: Object.values(devices.data), label: 'device'}]"
            :startPopulating="renderMap"
          />
        </div>
      </div>

      <!-- Installations -->
      <div class="device-group-devices mb-3">
        <div class="mt-8 min-h-full">
          <div class="device-group-devices-header flex justify-between mt-3">
            <div class="flex items-baseline">
              <h2 class="font-semibold text-2xl mr-2">
                {{ $t(`mygwf.components.Card.devices`) }}
              </h2>
              <span class="mr-4 text-lg">({{ filteredInstallations.length }})</span>
              <base-button-icon
                icon="refresh"
                size="sm"
                :loading="pendingDeviceRefresh"
                @click="onRefreshDevices"
              />
            </div>

            <!-- Action buttons -->
            <div
              class="action-buttons flex justify-end items-center space-x-3"
              :class="{invisible: isCreationMode}"
            >
              <!-- Device Add Success -->
              <div v-if="deviceAddSuccess" class="mx-2 text-sm shadow border py-1 px-2 flex">
                <div class="flex items-center text-green-600">
                  <base-icon icon="info" class="text-xl mr-3" />
                  <p>
                    {{ $t('mygwf.components.Notification.app.success.added') }}
                  </p>
                </div>
              </div>
              <!-- Device Add Error -->
              <div v-if="deviceAddError" class="mx-2 text-sm shadow border py-1 px-2 flex">
                <div class="flex items-center text-red-600">
                  <base-icon icon="info" class="text-xl mr-3" />
                  <p>
                    {{
                      $t('mygwf.components.Notification.app.info.devices_group_add_devices_info')
                    }}
                  </p>
                </div>
              </div>
              <!-- Unsaved changes -->
              <div
                v-if="hasPendingDeviceChanges"
                class="mr-4 text-sm shadow py-1 px-2 flex ease-in"
              >
                <div class="flex items-center text-primary">
                  <base-icon icon="info" class="text-xl mr-3" />
                  <p>
                    <span v-if="devices.added.length"
                      >Added {{ devices.added.length }} devices</span
                    >
                    <span v-if="devices.added.length && devices.removed.length"> | </span>
                    <span v-if="devices.removed.length"
                      >Removed {{ devices.removed.length }} devices</span
                    >
                  </p>
                </div>

                <!-- Listing actions -->
                <div :class="['ml-6 space-x-3', pendingRequest ? 'flex items-center' : '']">
                  <base-button
                    context="secondary"
                    action="discard"
                    size="sm"
                    :disabled="pendingRequest"
                    @click="onDiscardListingChanges"
                  />
                  <base-button
                    action="save"
                    size="sm"
                    :loading="pendingRequest"
                    :disabled="pendingRequest"
                    @click="onSaveDeviceChanges"
                  />
                </div>
              </div>

              <!-- Add devices -->
              <a-popover :visible="showAddDevicePopover" placement="bottomLeft">
                <template #content>
                  <base-input
                    v-model.trim="devices.toAdd"
                    :placeholder="$t('mygwf.components.Label.serial_add')"
                  />
                  <p class="text-sm -mt-2 text-gray-500">
                    {{ $t('mygwf.components.Label.serial_add_help') }}
                  </p>
                  <div class="flex justify-end mt-2 space-x-2">
                    <base-button-icon
                      icon="close"
                      tooltip="cancel"
                      @click="
                        showAddDevicePopover = false
                        devices.toAdd = ''
                      "
                    />
                    <base-button-icon
                      icon="checkmark"
                      class="bg-primary hover:bg-primary text-white"
                      tooltip="add"
                      :disabled="pendingDeviceRequest"
                      @click="onAddDevices"
                    />
                  </div>
                </template>

                <base-button
                  action="update_device_group_add_device"
                  size="sm"
                  :disabled="pendingRequest"
                  class="my-1"
                  :loading="pendingDeviceRequest"
                  @click="showAddDevicePopover = true"
                >
                  <base-icon icon="plus" />
                </base-button>
              </a-popover>
            </div>
          </div>

          <!-- Search device input -->
          <div class="search-device-input mt-3 relative">
            <base-input
              clearable
              v-model.trim="devices.searchTerm"
              :placeholder="$t(`mygwf.components.Label.serial_search`)"
            />
          </div>

          <hr v-if="form.installations.length" class="mb-5" />

          <!-- Listing -->
          <div class="listing overflow-y-auto" style="">
            <base-loader v-if="pendingComponentInit" fetching />
            <template v-else>
              <p v-if="!filteredInstallations.length" class="text-center">
                {{ $t(`mygwf.components.Card.no_devices`) }}
              </p>
              <div
                v-else
                v-for="device in filteredInstallations"
                :key="device"
                :class="{'bg-gray-100': devices.data[device].isNew}"
                class="flex items-center justify-between border-b p-3"
              >
                <div
                  class="grid grid-flow-col grid-cols-9 gap-4 justify-between w-full items-center"
                >
                  <!-- Meter info -->
                  <div class="flex flex-col space-y-1 truncate col-span-2">
                    <span class="text-xs text-secondary-dark">{{
                      $t(`mygwf.components.Label.meter`)
                    }}</span>
                    <span class="text-gray-600">
                      {{
                        devices.data[device].meter_serial ? devices.data[device].meter_serial : '-'
                      }}
                    </span>
                    <span class="text-sm text-gray-500">{{ devices.data[device].meter_type }}</span>
                  </div>

                  <!-- Commd info -->
                  <div class="flex flex-col space-y-1 truncate col-span-2">
                    <span class="text-xs text-secondary-dark">
                      {{ $t(`mygwf.components.Label.com_module`) }}</span
                    >
                    <span class="text-sm text-gray-600">
                      {{
                        devices.data[device].comd_serial ? devices.data[device].comd_serial : '-'
                      }}
                    </span>
                    <span class="text-sm text-gray-500">
                      {{ devices.data[device].comd_type ? devices.data[device].comd_type : '-' }}
                    </span>
                  </div>

                  <!-- Measuring Point info -->
                  <div class="flex flex-col truncate col-span-2">
                    <span class="text-xs text-secondary-dark">{{
                      $t(`mygwf.components.Label.measuring_point`)
                    }}</span>
                    <span class="text-gray-600">
                      {{ devices.data[device].mp_name ? devices.data[device].mp_name : '-' }}
                    </span>
                    <span class="text-gray-600 text-sm">
                      {{
                        devices.data[device].measuring_point_id
                          ? devices.data[device].measuring_point_id
                          : ''
                      }}
                    </span>
                    <p class="text-sm text-gray-500 mt-1">
                      <span class="text-xs">Lat:</span> <span>{{ devices.data[device].lat }}</span>
                    </p>
                    <p class="text-sm text-gray-500">
                      <span class="text-xs">Lng:</span> <span>{{ devices.data[device].lng }}</span>
                    </p>
                  </div>

                  <!-- Readouts info -->
                  <div class="flex flex-col space-y-3 truncate col-span-2">
                    <span class="text-xs text-secondary-dark">{{
                      $t(`mygwf.components.Label.readouts`)
                    }}</span>
                    <p class="text-sm text-gray-500">
                      <span class="text-xs">Total Volume: </span>
                      <span>{{ devices.data[device].state.volume }}</span>
                    </p>
                    <p class="text-sm text-gray-500">
                      <span class="text-xs">Last Readout: </span>
                      <span>{{ $d(new Date(devices.data[device].state.last_entry), 'long') }}</span>
                    </p>
                  </div>

                  <!-- Remove action -->
                  <div class="flex items-center justify-end">
                    <base-icon
                      icon="close"
                      class="cursor-pointer hover:bg-gray-100 rounded p-1 text-gray-700 text-2xl"
                      @click="onRemoveDevice(device)"
                    />
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </base-card>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import _uniq from 'lodash-es/uniq.js'
import _pick from 'lodash-es/pick.js'
import fileExport from '@/state/helpers/fileExport.js'
import {required, integer, minValue} from 'vuelidate/lib/validators'
import Popover from 'ant-design-vue/lib/popover'
import {
  BaseCard,
  BaseInput,
  BaseCheck,
  BaseMap,
  BaseButton,
  BaseLoader,
  BaseIcon,
  BaseSelect,
  BaseButtonIcon,
} from '@/components/base'

export default {
  components: {
    BaseCard,
    BaseMap,
    BaseLoader,
    BaseCheck,
    BaseIcon,
    BaseInput,
    BaseButton,
    BaseSelect,
    BaseButtonIcon,
    'a-popover': Popover,
  },

  props: {
    preloadedDevices: {
      type: Array,
      default: [],
    },
  },

  data() {
    return {
      pendingComponentInit: false,
      pendingRequest: false,
      pendingExport: false,
      pendingDeviceRequest: false,
      pendingDeviceRefresh: false,
      pendingDelete: false,
      pendingCompanyRevert: false,

      renderMap: false,
      showAddDevicePopover: false,
      isGroupEditable: false,
      deviceAddError: false,
      deviceAddSuccess: false,

      companies: this.$store.getters['companies/list'],

      devices: {
        pagination: {
          total: 0,
          next: null,
          previous: null,
        },
        data: {},
        searchTerm: '',
        toAdd: '',
        added: [],
        removed: [],
      },
      form: null,
      aggregatedData: {
        total_volume: '-',
        total_alerts: '-',
      },
    }
  },

  computed: {
    ...mapGetters('deviceGroups', {
      editedGroupUuid: 'editedGroupUuid',
      mode: 'editedGroupMode',
    }),

    isCreationMode() {
      return this.mode === 'creation'
    },

    filteredInstallations() {
      if (!this.form?.installations || !this.form.installations.length) {
        return []
      }
      let arr = []
      this.form.installations.forEach(uuid => {
        if (this.devices.data[uuid]?.meter_serial.includes(this.devices.searchTerm)) {
          arr.push(this.devices.data[uuid])
        }
      })
      arr = arr.sort((a, b) => b.isNew - a.isNew)
      return arr.map(e => e.uuid)
    },

    hasPendingDeviceChanges() {
      return this.devices.added.length || this.devices.removed.length
    },
  },

  watch: {
    editedGroupUuid() {
      this._initComponent()
    },
  },

  created() {
    this._initComponent()
  },

  destroyed() {
    this.$store.dispatch('deviceGroups/clearEditedGroup')
  },

  methods: {
    // ================================================================================
    // Events
    // ================================================================================

    // ====================
    // Group info
    // ====================

    async onGroupSubmit() {
      if (this.pendingRequest || this.$v.form.$invalid) {
        return
      }

      const creationMode = this.mode === 'creation'
      const payload = _pick(this.form, ['uuid', 'name', 'description', 'company'])
      let group
      try {
        this.pendingRequest = true
        group = await this.$store.dispatch(
          `deviceGroups/${creationMode ? 'create' : 'update'}`,
          payload
        )
        await this.$store.dispatch('app/notification', {
          type: 'success',
          group: 'app',
          text: creationMode ? 'created' : 'updated',
        })
        if (creationMode) {
          this.form.uuid = group.uuid
          await this.onSaveDeviceChanges()
          await this.$store.dispatch('deviceGroups/updateEditedGroupMode', 'edit')
        }
        this.isGroupEditable = false
      } catch (errors) {
        await this.$store.dispatch('app/notification', {
          type: 'error',
          group: 'app',
          text: creationMode ? 'created' : 'updated',
        })
      } finally {
        this.pendingRequest = false
      }
    },

    async onGroupDelete() {
      if (this.pendingRequest) {
        return
      }
      const shouldDelete = confirm(
        this.$t(`mygwf.components.Confirmation.device_group_delete`, {name: this.form.name})
      )
      if (!shouldDelete) {
        return
      }

      try {
        this.pendingRequest = true
        this.pendingDelete = true
        await this.$store.dispatch('deviceGroups/delete', this.form.uuid)
        await this.$store.dispatch('app/notification', {
          type: 'success',
          group: 'app',
          text: 'deleted',
        })
      } catch (e) {
        await this.$store.dispatch('app/notification', {
          type: 'error',
          group: 'app',
          text: 'deleted',
        })
      } finally {
        this.pendingRequest = false
        this.pendingDelete = false
        this.onCloseComponent()
      }
    },

    // ====================
    // Devices
    // ====================

    onAddPreloadedDevices() {
      if (!this.preloadedDevices.length) {
        return
      }
      const company = this.$store.getters['companies/getByName'](this.preloadedDevices[0].company)
      if (!company) {
        return
      }

      // Set company for group
      this.form.company = company.id

      // Filter out devices who does not to same company
      this.preloadedDevices.filter(e => e.company === company.name)

      // Load devices to group
      this.preloadedDevices.forEach(e => {
        this.form.installations.push(e.uuid)
        this.devices.data[e.uuid] = {...e, isNew: true}
        this.devices.added.push(e.uuid)
      })
    },

    async onAddDevices() {
      try {
        if (!this.devices.toAdd || this.pendingDeviceRequest) {
          return
        }
        this.pendingDeviceRequest = true

        const result = {
          requested: 0,
          duplicates: 0,
          exist: 0,
          fetched: 0,
        }

        // Remove trailing comma
        if (this.devices.toAdd.slice(-1) === ',') {
          this.devices.toAdd = this.devices.toAdd.slice(0, -1)
        }

        // Extract serials to add
        let serials = this.devices.toAdd.replaceAll(' ', '').split(',')
        result.requested = serials.length

        // Remove duplicates
        serials = _uniq(serials)
        result.duplicates = result.requested - serials.length

        // Remove existing serials
        const existingSerials = this.form.installations.map(e => this.devices.data[e].meter_serial)
        serials = serials.filter(e => !existingSerials.includes(e))
        result.exist = result.requested - result.duplicates - serials.length
        if (serials.length === 0) {
          this.deviceAddError = true
          setTimeout(() => {
            this.deviceAddError = false
          }, 5000)
          return
        }

        // Fetch device data
        const fetchedDevices = await this.fetchDeviceData('meter_serial', serials, true)
        result.fetched = fetchedDevices.length
        const fetchedDevicesUuids = fetchedDevices.map(e => e.uuid)
        this.form.installations = _uniq([...this.form.installations, ...fetchedDevicesUuids])
        this.devices.added.push(...fetchedDevicesUuids)

        // User feedback
        if (result.fetched > 0) {
          this.deviceAddSuccess = true
          setTimeout(() => {
            this.deviceAddSuccess = false
          }, 5000)
        }
        if (result.requested !== result.fetched) {
          this.deviceAddError = true
          setTimeout(() => {
            this.deviceAddError = false
          }, 5000)
        }
      } catch (e) {
        console.log(e)
      } finally {
        this.devices.toAdd = ''
        this.renderMarkersOnMap()
        this.showAddDevicePopover = false
        this.pendingDeviceRequest = false
      }
    },

    onRemoveDevice(uuid) {
      this.form.installations = this.form.installations.filter(e => e !== uuid)
      if (this.devices.data[uuid].isNew) {
        this.devices.added = this.devices.added.filter(e => e !== uuid)
        this.devices.data[uuid].isNew = false
      } else {
        this.devices.removed.push(uuid)
      }
      this.renderMarkersOnMap()
    },

    onDiscardListingChanges() {
      // Remove added devices from installations
      this.form.installations = this.form.installations.filter(e => !this.devices.added.includes(e))
      this.devices.added = []
      // Add removed devices to installations
      this.form.installations.push(...this.devices.removed)
      this.devices.removed = []
    },

    async onRefreshDevices() {
      if (this.pendingRequest) {
        return
      }

      try {
        this.pendingRequest = true
        this.pendingDeviceRefresh = true
        await this.fetchDeviceData('device_group')
      } catch (e) {
      } finally {
        this.pendingRequest = false
        this.pendingDeviceRefresh = false
      }
    },

    async onSaveDeviceChanges() {
      try {
        this.pendingRequest = true

        // Remove devices
        this.devices.removed = this.devices.removed.filter(e => !this.devices.added.includes(e))
        if (this.devices.removed.length) {
          await this.$store.dispatch('deviceGroups/removeDevices', {
            group_uuid: this.form.uuid,
            device_uuids: this.devices.removed,
          })
        }

        // Add devices
        if (this.devices.added.length) {
          await this.$store.dispatch('deviceGroups/addDevices', {
            group_uuid: this.form.uuid,
            device_uuids: this.devices.added,
          })
        }

        // Normalize device data
        this.devices.added.forEach(e => (this.devices.data[e].isNew = false))
        this.devices.removed.forEach(e => delete this.devices.data[e])

        // Reset state
        this.devices.added = []
        this.devices.removed = []

        await this.fetchGroupAggregatedData()
      } catch (e) {
        console.log(e)
      } finally {
        this.pendingRequest = false
      }
    },

    onCloseComponent() {
      this.$store.dispatch('deviceGroups/clearEditedGroup')
      this.$emit('close')
    },

    // ================================================================================
    // Helpers
    // ================================================================================

    async fetchDeviceData(searchBy, identifiers = [], isNew = false) {
      try {
        const fetchedDevices = []

        const payload =
          searchBy === 'device_group'
            ? {groups: this.form.uuid, limit: 9999, offset: 0}
            : {meters: identifiers.join(';'), company: this.form.company}

        const response = await this.$store.dispatch('devices/getDevices', payload)
        response.results = response.results.map(item => {
          return {...item, state: {...item.state, alertCount: this.alertsCount(item)}}
        })
        response.results.forEach(e => {
          fetchedDevices.push(e)
          this.$set(this.devices.data, e.uuid, {...e, isNew})
        })
        return Promise.resolve(fetchedDevices)
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async fetchGroupAggregatedData() {
      try {
        const data = await this.$store.dispatch('deviceGroups/getAggregatedData', this.form.uuid)
        this.aggregatedData.total_volume = data.total_usage
        this.aggregatedData.total_alerts = data.total_alerts
      } catch (e) {
        return Promise.reject(e)
      }
    },

    renderMarkersOnMap() {
      this.renderMap = false
      this.$nextTick(() => (this.renderMap = true))
    },
    alertsCount(device) {
      const deviceAlerts = this.deviceAlerts(device)
      return Object.values(deviceAlerts).filter(item => item).length
    },
    deviceAlerts(device) {
      const {volume, last_entry, battery_lifetime, alertCount, ...deviceAlerts} = device.state
      return deviceAlerts
    },
    async _initComponent() {
      try {
        window.scrollTo(0, 0)
        this.pendingComponentInit = true
        this.isGroupEditable = false
        this.devices.toAdd = ''
        this.devices.data = {}
        this.aggregatedData = {total_volume: '-', total_alerts: '-'}
        this.form = this.$store.getters['deviceGroups/editedGroup']
        if (this.mode === 'creation') {
          this.isGroupEditable = true
          this.onAddPreloadedDevices()
        }
        if (this.mode === 'edit') {
          await this.fetchDeviceData('device_group', this.form.installations)
          await this.fetchGroupAggregatedData()
        }
      } catch (e) {
        console.log(e)
        this.onCloseComponent()
      } finally {
        this.pendingComponentInit = false
        this.$nextTick(() => this.renderMarkersOnMap())
      }
    },
  },

  // ================================================================================
  // Validation
  // ================================================================================
  validations: {
    form: {
      name: {required},
      company: {required, integer, minValue: minValue(1)},
    },
  },
}
</script>
